import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { RiArrowRightLine, RiArrowLeftLine, RiCloseLine } from 'react-icons/ri';
import { TOUR_STEPS } from './steps';
import KitsuMark from '../brand/KitsuMark';

/* ══════════════════════════════════════════════════════════════
   TOUR OVERLAY

   A spotlight tour: the screen dims, one piece of real UI stays lit,
   and a card explains it. Pointing at the actual sidebar teaches far
   more than a carousel of screenshots.

   Three things this has to survive, all of which broke naive versions:

   • A target that is not on screen. The sidebar collapses to a drawer
     below 1024px, so half these hooks have no box on a phone. Rather
     than spotlighting nothing, a step with no measurable target falls
     back to a centred card and still reads correctly.

   • Layout that moves under it. Fonts load, the path animates in, the
     window resizes. The hole is re-measured on a rAF loop while a step
     is open rather than once on mount.

   • Steps on other routes. A step can name a route and the tour
     navigates before measuring, waiting for the target to actually
     appear instead of assuming it is there on the next frame.
══════════════════════════════════════════════════════════════ */

/* Is this element clipped out of sight by a collapsed ancestor?

   A non-zero getBoundingClientRect is not proof that anything is visible.
   The learn path collapses a section with `grid-template-rows: 0fr` plus
   `overflow: hidden` on the inner div: the grid row shrinks to nothing but
   the node inside keeps its full layout box, so it still measures 68x68
   while being completely invisible. The tour happily spotlighted that —
   a small lit rectangle floating between two collapsed section cards,
   pointing at nothing.

   Only `hidden`/`clip` ancestors count. A scrollable ancestor (`auto` or
   `scroll` — `.app-main` here) also puts things out of view, but that is a
   scroll position, which the tour fixes by scrolling; treating it as
   invisible would stop the tour ever scrolling anything into view. */
function isClippedAway(el) {
  const r = el.getBoundingClientRect();
  let node = el.parentElement;
  while (node && node !== document.body) {
    const style = getComputedStyle(node);
    const overflow = `${style.overflow} ${style.overflowX} ${style.overflowY}`;
    if (/hidden|clip/.test(overflow)) {
      const box = node.getBoundingClientRect();
      /* Collapsed to nothing vertically — the `grid-template-rows: 0fr`
         case, which is the whole reason this function exists. */
      if (box.height < 1) return true;
      /* Or pushed entirely outside a container that really does clip.
         Vertical only, deliberately: `.learn-collapse > div` is a grid
         item with no explicit column, so it measures zero *width* even
         when fully open. A horizontal test against that reported every
         open node as clipped — the fix breaking worse than the bug. */
      const overlapY = Math.min(r.bottom, box.bottom) - Math.max(r.top, box.top);
      if (overlapY < Math.min(8, r.height)) return true;
    }
    node = node.parentElement;
  }
  return false;
}

/* The same hook can legitimately appear twice: the streak pill lives in
   the desktop topbar *and* in the mobile header, and CSS hides whichever
   one does not apply. `querySelector` returns the first in document
   order, which on desktop is the display:none mobile header — a 0x0 box
   that fails every sanity check below, so the streak step fell back to a
   centred card and lit nothing. Take the first match that is actually
   rendered and visible, not the first that exists. */
function findTarget(selector) {
  if (!selector) return null;
  const all = document.querySelectorAll(selector);
  for (const el of all) {
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0 && !isClippedAway(el)) return el;
  }
  return null;
}

/* The nearest ancestor that actually scrolls — `.app-main` in this app.
   Needed because the tour scrolls a target into a chosen band rather
   than just calling scrollIntoView, which can only centre. */
function scrollParentOf(el) {
  let node = el?.parentElement;
  while (node && node !== document.body) {
    const style = getComputedStyle(node);
    if (/(auto|scroll)/.test(style.overflowY) && node.scrollHeight > node.clientHeight + 4) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/* A fixed element cannot be scrolled into a band — the topbar streak pill
   is already where it is going to be, and scrolling the page to chase it
   just moves the content underneath for no reason. */
function isFixed(el) {
  let node = el;
  while (node && node !== document.body) {
    if (getComputedStyle(node).position === 'fixed') return true;
    node = node.parentElement;
  }
  return false;
}

const PAD = 8;          // breathing room around the highlighted element
const CARD_W = 330;
const GAP = 14;         // between the hole and the card
/* Matches the sidebar's own breakpoint in AppSidebar.jsx: below this the
   sidebar is a drawer rather than a column. */
const MOBILE = 1024;

export default function Tour({ onClose, setSidebarOpen }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [index, setIndex] = useState(0);
  /* Three states, deliberately:
       object     — spotlight this box
       null       — no target; show the centred card
       undefined  — pending: the target may still be arriving, so do not
                    commit to the centred layout yet */
  const [rect, setRect] = useState(null);
  const raf = useRef(0);
  const cardRef = useRef(null);
  /* The docked card's real height. The old rule — dock to the top when the
     target sits below the halfway line — guessed at it, and guessed wrong
     for anything near the middle: a target at 51% got a bottom-docked card
     that covered it anyway. Measuring turns a guess into an overlap test. */
  const [cardH, setCardH] = useState(0);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= MOBILE);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const step = TOUR_STEPS[index];
  const isLast = index === TOUR_STEPS.length - 1;

  /* Navigate first if the step lives elsewhere. */
  useEffect(() => {
    if (step.route && location.pathname !== step.route) {
      navigate(step.route);
    }
  }, [step, location.pathname, navigate]);

  /* On a phone the sidebar is a drawer, so a step pointing at a nav item
     has nothing to point at until it is open. Opening it turns those
     steps from a dimmed screen with floating text into an actual
     spotlight on the item being described. */
  useEffect(() => {
    if (!isMobile || !setSidebarOpen) return undefined;
    setSidebarOpen(Boolean(step.sidebar));
    return undefined;
  }, [step, isMobile, setSidebarOpen]);

  /* Leave the drawer as we found it when the tour ends. */
  useEffect(() => () => { setSidebarOpen?.(false); }, [setSidebarOpen]);

  /* Bring the target on screen before pointing at it. Without this a
     step whose target has scrolled away — the streak pill in the topbar,
     once the learn path is scrolled down — measures as off-screen, and
     the tour quietly falls back to a centred card that explains
     something the reader cannot see.

     Retried for a few frames because the element may not exist yet: the
     step above may still be navigating to its route. */
  useEffect(() => {
    if (!step.target) return undefined;
    let tries = 0;
    let timer = 0;
    const bring = () => {
      const el = findTarget(step.target);
      if (el) {
        /* Not scrollIntoView({block:'center'}). On a phone the card is
           docked across the bottom of the screen, so a target parked at
           50% sits behind it — which is exactly what happened to the
           current node on the learn path. Aim for the upper third
           instead, where the card cannot reach. */
        if (isFixed(el)) return;
        const container = scrollParentOf(el);
        const box = el.getBoundingClientRect();
        const wantCentre = window.innerHeight * (isMobile ? 0.30 : 0.5);
        const delta = (box.top + box.height / 2) - wantCentre;
        if (Math.abs(delta) < 24) return;
        if (container) {
          container.scrollTo({ top: container.scrollTop + delta, behavior: 'smooth' });
        } else {
          el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
        }
        return;
      }
      /* The learn path opens its active section when a tour starts, and
         that expand takes 380ms — so the target is legitimately not
         findable for the first few hundred milliseconds. Outlast it. */
      if (++tries < 30) timer = setTimeout(bring, 60);
    };
    bring();
    return () => clearTimeout(timer);
  }, [step, isMobile]);

  /* Measure continuously while the step is open: the page is still
     settling (fonts, entry animations) when the step first mounts, and a
     single measurement lands on the wrong box. */
  useEffect(() => {
    let alive = true;
    /* A target is not always in place the instant a step opens: the
       sidebar drawer takes ~180ms to slide in, and a scrolled-away
       target has to travel. Falling back to the centred card the moment
       it fails to measure means the first step after the drawer opens
       explains a nav item without ever pointing at it. Hold off on that
       decision for a beat and let the target arrive. */
    const startedAt = Date.now();
    const GRACE_MS = 900;
    setRect(undefined);
    const measure = () => {
      if (!alive) return;
      if (!step.target) {
        setRect(prev => (prev === null ? prev : null));
      } else {
        const el = findTarget(step.target);
        const r = el?.getBoundingClientRect();
        /* Horizontal bounds matter as much as vertical: below 1024px the
           sidebar is a drawer translated off to the left. It still has a
           box, so a vertical-only check happily spotlights nav items the
           user cannot see. */
        const usable = r && r.width > 0 && r.height > 0
          && r.bottom > 4 && r.top < window.innerHeight - 4
          && r.right > 4 && r.left < window.innerWidth - 4;
        /* A hole larger than the screen dims nothing, so an oversized
           target is treated as no target and the card centres instead. */
        const sane = usable
          && r.height < window.innerHeight * 0.8
          && r.width < window.innerWidth * 0.9;
        setRect(prev => {
          if (!sane) {
            /* Inside the grace period this becomes `undefined`, not the
               previous step's box — holding `prev` would leave the
               spotlight sitting on the item we just finished explaining. */
            if (Date.now() - startedAt < GRACE_MS) return undefined;
            return prev === null ? prev : null;
          }
          if (prev && Math.abs(prev.top - r.top) < 0.5 && Math.abs(prev.left - r.left) < 0.5
              && Math.abs(prev.width - r.width) < 0.5) return prev;
          return { top: r.top, left: r.left, width: r.width, height: r.height };
        });
      }
      raf.current = requestAnimationFrame(measure);
    };
    measure();

    /* A timer as well as the frame loop. rAF is the right clock for
       tracking a target smoothly through a scroll, but it stops dead
       whenever the page is not being painted — a backgrounded tab, or
       any environment that does not composite. If the drawer opens
       during one of those stretches, the frame loop never wakes to
       notice and the step explains a nav item it never points at.
       The timer costs one getBoundingClientRect and removes that
       dependency entirely. */
    const backstop = setInterval(measure, 120);

    return () => {
      alive = false;
      cancelAnimationFrame(raf.current);
      clearInterval(backstop);
    };
  }, [step]);

  /* Re-measured per step because the body text length changes the card's
     height, and a stale height makes the overlap test wrong in both
     directions. */
  useEffect(() => {
    const read = () => setCardH(cardRef.current?.offsetHeight || 0);
    read();
    const t = setTimeout(read, 60);   // after the entrance transition settles
    return () => clearTimeout(t);
  }, [index, isMobile, rect]);

  /* Would a bottom-docked card sit on top of the thing it is describing?
     If so, dock it to the top instead. */
  const dockTop = isMobile && !!rect && cardH > 0
    && rect.top + rect.height + PAD > window.innerHeight - cardH - 20;

  const close = useCallback(() => { onClose?.(); }, [onClose]);

  const next = useCallback(() => {
    if (isLast) {
      /* Finishing leaves the reader where they are — on the learn path,
         looking at the thing the tour just told them to start. */
      close();
      return;
    }
    setIndex(i => i + 1);
  }, [isLast, close]);

  const back = useCallback(() => setIndex(i => Math.max(0, i - 1)), []);

  useEffect(() => {
    const onKey = e => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight' || e.key === 'Enter') next();
      if (e.key === 'ArrowLeft') back();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close, next, back]);

  /* Card position, flipped away from whichever edge it would spill over. */
  const cardStyle = () => {
    /* On a phone the card is pinned to the bottom, always. There is no
       room beside a spotlight on a 375px screen, and every attempt to
       place it relative to the target ends with an edge off the screen.
       A fixed dock is both predictable and always fully visible. */
    if (isMobile) return null;

    /* Centring is done with inset+margin, not translate(-50%,-50%): the
       entrance keyframes animate `transform`, and with fill-mode `both`
       they overwrite the centring transform the moment they finish —
       leaving the card hanging half off the right edge. */
    if (!rect) return null;
    const place = step.placement || 'right';
    let top = rect.top;
    let left = rect.left + rect.width + GAP + PAD;

    if (place === 'bottom' || left + CARD_W > window.innerWidth - 12) {
      left = Math.min(
        Math.max(12, rect.left + rect.width / 2 - CARD_W / 2),
        window.innerWidth - CARD_W - 12,
      );
      top = rect.top + rect.height + GAP + PAD;
    }
    /* If it would run off the bottom, sit it above the target instead. */
    if (top + 210 > window.innerHeight) {
      top = Math.max(12, window.innerHeight - 220);
    }
    return { top, left };
  };

  return createPortal(
    <div className="tour-root" role="dialog" aria-modal="true" aria-label="Product tour">
      {/* The dim layer *is* the hole: an enormous spread shadow around a
          transparent box. One element, no SVG mask, and the lit area is
          genuinely see-through rather than a lighter patch. */}
      <div
        className={`tour-spot${rect ? '' : ' tour-spot-full'}`}
        style={rect ? {
          top: rect.top - PAD,
          left: rect.left - PAD,
          width: rect.width + PAD * 2,
          height: rect.height + PAD * 2,
        } : undefined}
      />

      <div
        ref={cardRef}
        className={[
          'tour-card',
          isMobile
            /* Dock away from the target, not on top of it. A card pinned
               to the bottom sits directly over anything highlighted in
               the lower half of the screen — the Account item at the
               foot of the drawer being the obvious case. */
            ? `tour-card-docked${dockTop ? ' top' : ''}`
            : (rect ? '' : 'tour-card-centred'),
        ].filter(Boolean).join(' ')}
        style={cardStyle() || undefined}
      >
        <button className="tour-skip" onClick={close} aria-label="Skip the tour">
          <RiCloseLine size={16} />
        </button>

        <div className="tour-card-head">
          <KitsuMark size={30} interactive={false} animation={isLast ? 'breathe' : null} />
          <span className="tour-count">{index + 1} of {TOUR_STEPS.length}</span>
        </div>

        <h3 className="tour-title">{step.title}</h3>
        <p className="tour-body">{step.body}</p>

        <div className="tour-actions">
          {index > 0 && (
            <button className="btn-ghost tour-back" onClick={back}>
              <RiArrowLeftLine size={15} /> Back
            </button>
          )}
          <button className="btn-primary tour-next" onClick={next}>
            {step.cta || (isLast ? 'Done' : 'Next')} <RiArrowRightLine size={15} />
          </button>
        </div>

        <div className="tour-dots" aria-hidden="true">
          {TOUR_STEPS.map((s, i) => (
            <i key={s.id} className={i === index ? 'on' : i < index ? 'past' : ''} />
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}
